import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ordersApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import { DATE_RANGES } from '@/lib/dateRanges'
import { getPreference, setPreference } from '@/lib/preferences'
import { saveBlob } from '@/lib/download'
import Icon from '@/components/common/Icons'
import {
  Empty,
  ErrorNote,
  Loading,
  PageHeader,
  Pagination,
  SourceBadge,
  StatusBadge,
  Toolbar,
} from '@/components/common/Ui'
import { ORDER_STATUS_LABELS, SOURCE_LABELS, dateTime, money } from '@/lib/format'
import { PERMS, can, isSuperAdmin, useAuth } from '@/store/auth'
import NewOrder from '@/components/orders/NewOrder'
import { Alert } from '@/components/common/Ui'

const PAGE_SIZE = 50

const DATES_PREFERENCE = 'orders.dates'

/** Midnight at the start of today on the viewer's clock, as an instant. */
const startOfToday = () => {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d.toISOString()
}

/**
 * The sortable columns, with the direction a first click sorts in.
 *
 * Numbers and dates open largest/newest first, since that is what someone
 * clicking 'Total' or 'Created' is looking for; words open A–Z.
 */
const COLUMNS = [
  { key: 'order_number', label: 'Order', first: 'asc' },
  { key: 'customer', label: 'Customer', first: 'asc' },
  { key: 'source', label: 'Source', first: 'asc' },
  { key: 'status', label: 'Status', first: 'asc' },
  { key: 'total', label: 'Total', first: 'desc', right: true },
  { key: 'created_at', label: 'Created', first: 'desc' },
]

/**
 * Which range the list opens on.
 *
 * Today unless this user has picked something else, in which case theirs —
 * the counter staff live in today, the owner lives in the month, and
 * re-picking it every visit is the kind of small tax nobody reports.
 *
 * Not remembered: 'custom'. Its from/to are two separate fields, and
 * restoring "1–7 September" in November would silently show an empty list
 * that looks like a broken page rather than an old filter.
 */
function openingRange(userId) {
  const saved = getPreference(userId, DATES_PREFERENCE)
  // Validated against the presets that exist now, so a key dropped in a
  // later version degrades to today instead of an empty dropdown.
  if (saved && saved !== 'custom' && saved in DATE_RANGES) return saved
  return 'today'
}

/**
 * Two stacked triangles: both faint on a column that is not sorted, so every
 * header shows it can be clicked, and the one pointing the current way solid
 * on the column that is.
 *
 * Drawn rather than typed: the '↕' glyph falls back to a font that renders
 * it too thin to see beside 12px capitals.
 */
function SortIcon({ state }) {
  return (
    <svg className="sort-icon" width="8" height="12" viewBox="0 0 8 12" aria-hidden="true">
      <path d="M4 0 8 5H0z" opacity={state === 'asc' ? 1 : 0.35} />
      <path d="M4 12 0 7h8z" opacity={state === 'desc' ? 1 : 0.35} />
    </svg>
  )
}

export default function Orders() {
  // The sidebar links here with ?review=1, so the filter starts from the
  // URL: a badge that lands on an unfiltered list would be lying about what
  // it counted. It is its own parameter rather than a status, because what
  // needs review is a property of an order's lines — 'pending' covers every
  // unconfirmed order, reviewed or not.
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useAuth()
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || '',
    // 'Later orders': accepted but not yet delivered, and in that state
    // since before today — see filterParams. In the URL, like status,
    // so the view can be linked to and survives a reload.
    later: searchParams.get('later') === '1',
    // Customers who reported items not delivered, still unresolved.
    issue: searchParams.get('issue') === '1',
    source: '',
    search: '',
    // Whatever this user last chose, or today.
    //
    // Except when the URL names a status. The sidebar's 'Orders 9' badge
    // links here with ?status=needs_review, and those nine include orders
    // from previous days — landing on a date filter would show a count of 9
    // beside a list of none, which is worse than either.
    //
    // The same for the dashboard's 'Open review queue' (?review=1): its count
    // covers every day, so the list has to as well.
    //
    // And for later orders: they are by definition from before today, so a
    // created-today filter would always leave the list empty.
    dates:
      searchParams.get('status') ||
      searchParams.get('review') === '1' ||
      searchParams.get('later') === '1' ||
      searchParams.get('issue') === '1'
        ? ''
        : openingRange(user?.id),
    date_from: '',
    date_to: '',
  })
  const [page, setPage] = useState(1)
  // '' is the backend's own order (newest first, or least confident first
  // in the review queue); otherwise a column key, '-' prefixed for
  // descending — the same string the API takes.
  const [sort, setSort] = useState('')
  const [exporting, setExporting] = useState('')
  const [error, setError] = useState('')
  const [creating, setCreating] = useState(false)
  // The API creates an order for a role that can correct orders.
  const canCreate = can(user, PERMS.ordersWrite) || isSuperAdmin(user)

  // 'Needs review' is not just another status filter: the backend sorts
  // those worst-confidence first, so the riskiest parse is the first row a
  // reviewer sees. This is what the separate review-queue page used to be.
  const reviewing = searchParams.get('review') === '1'

  // A preset is just a shorthand for a from/to pair, so the two paths meet
  // here and everything downstream sees plain dates.
  const preset = DATE_RANGES[filters.dates] ?? DATE_RANGES['']
  const dateRange =
    filters.dates === 'custom'
      ? {
          ...(filters.date_from ? { date_from: filters.date_from } : {}),
          ...(filters.date_to ? { date_to: filters.date_to } : {}),
        }
      : preset.range()

  const filterParams = {
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.source ? { source: filters.source } : {}),
    ...(filters.search ? { search: filters.search } : {}),
    ...(reviewing ? { needs_review: true } : {}),
    // Confirmed, packed or dispatched, and in that status since before the
    // start of today on the viewer's clock — dispatched two days ago and
    // still not delivered is here; dispatched this morning is not. Decided
    // by status and its history, never by the requested delivery date.
    ...(filters.later ? { pending_before: startOfToday() } : {}),
    ...(filters.issue ? { delivery_issue: true } : {}),
    ...dateRange,
  }
  // Later orders open oldest first — the one waiting longest leads — unless
  // a column was clicked.
  const effectiveSort = sort || (filters.later ? 'created_at' : '')
  const params = {
    page,
    page_size: PAGE_SIZE,
    ...filterParams,
    ...(effectiveSort ? { sort: effectiveSort } : {}),
  }

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['orders', params],
    queryFn: () => ordersApi.list(params),
    // Polled while reviewing, so work arriving from WhatsApp shows up
    // without anyone reloading the page.
    refetchInterval: reviewing ? 20_000 : false,
  })

  // Whether anything is narrowing the list, so the count can say whether it
  // Whether anything is narrowing the list, so Clear filters knows there is
  // something to clear — including when the operator arrived from the
  // sidebar's review link rather than by choosing a filter.
  const anyFilter =
    Object.keys(filterParams).some((k) => k !== 'needs_review') || reviewing

  const clearFilters = () => {
    setFilters({
      status: '', later: false, issue: false, source: '', search: '', dates: '', date_from: '',
      date_to: '',
    })
    setPage(1)
    // The status lives in the URL so the list can be linked to; clearing the
    // filters has to clear that too, or a reload brings them back.
    setSearchParams({}, { replace: true })
  }

  const runExport = async (format) => {
    setExporting(format)
    setError('')
    try {
      const { blob, filename } = await ordersApi.export(
        { ...filterParams, ...(effectiveSort ? { sort: effectiveSort } : {}) },
        format,
      )
      saveBlob(blob, filename)
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setExporting('')
    }
  }

  // The column and direction the list is actually in, for the header arrow:
  // with no sort chosen, outside the review queue, that is newest first.
  const activeSort = effectiveSort || (reviewing ? '' : '-created_at')

  const sortBy = (column) => {
    const current = activeSort.replace(/^-/, '')
    const descending = activeSort.startsWith('-')
    const next =
      current === column.key
        ? descending ? column.key : `-${column.key}`
        : column.first === 'desc' ? `-${column.key}` : column.key
    setSort(next)
    setPage(1)
  }

  const update = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value }))
    setPage(1)
    // Kept in the URL so the filtered list survives a reload and can be
    // linked to — including by the sidebar.
    if (key === 'status' || key === 'later' || key === 'issue') {
      const next = new URLSearchParams(searchParams)
      next.delete('review')
      if (key === 'status') {
        if (value) next.set('status', value)
        else next.delete('status')
      } else if (value) next.set(key, '1')
      else next.delete(key)
      setSearchParams(next, { replace: true })
    }
  }

  /**
   * Later orders on: the created-date filter goes to 'Any date', or the
   * default 'Today' would hide every one of them. Not saved as the range
   * preference — that is only ever set from the dropdown.
   */
  const toggleLater = (on) => {
    update('later', on)
    if (on) setFilters((f) => ({ ...f, dates: '', date_from: '', date_to: '' }))
    // A sort picked for the old view (say, by total) would hide 'oldest
    // first', which is the point of this one.
    setSort('')
  }

  /**
   * Delivery issues on: like Later orders, any created date — a report can
   * come days after the order was placed.
   */
  const toggleIssue = (on) => {
    update('issue', on)
    if (on) setFilters((f) => ({ ...f, dates: '', date_from: '', date_to: '' }))
  }

  /**
   * Picking a range from the dropdown also sets what this list opens on
   * next time. Only from the dropdown: clearing the filters is a reset for
   * the visit, not a statement about how the page should greet you.
   *
   * 'custom' is applied but not stored — see openingRange.
   */
  const chooseDates = (value) => {
    update('dates', value)
    if (value !== 'custom') setPreference(user?.id, DATES_PREFERENCE, value)
  }

  return (
    <div className="orders-page">
      <PageHeader
        title="Orders"
        actions={
          canCreate && (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>
              Create order
            </button>
          )
        }
      />
      <NewOrder open={creating} onClose={() => setCreating(false)} />

      <Toolbar>
        <input
          type="search"
          placeholder="Order number, customer or phone…"
          value={filters.search}
          onChange={(e) => update('search', e.target.value)}
        />
        <select value={filters.status} onChange={(e) => update('status', e.target.value)}>
          <option value="">All statuses</option>
          {Object.entries(ORDER_STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select value={filters.source} onChange={(e) => update('source', e.target.value)}>
          <option value="">All sources</option>
          {Object.entries(SOURCE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select value={filters.dates} onChange={(e) => chooseDates(e.target.value)}>
          {Object.entries(DATE_RANGES).map(([value, { label }]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        {filters.dates === 'custom' && (
          <>
            <input
              type="date"
              aria-label="From date"
              value={filters.date_from}
              onChange={(e) => update('date_from', e.target.value)}
            />
            <input
              type="date"
              aria-label="To date"
              value={filters.date_to}
              onChange={(e) => update('date_to', e.target.value)}
            />
          </>
        )}

        <label
          className="checkbox"
          title="Confirmed, packed or dispatched before today and still not delivered"
        >
          <input
            type="checkbox"
            checked={filters.later}
            onChange={(e) => toggleLater(e.target.checked)}
          />
          Later orders
        </label>

        <label
          className="checkbox"
          title="Customers who reported items not delivered, not yet resolved"
        >
          <input
            type="checkbox"
            checked={filters.issue}
            onChange={(e) => toggleIssue(e.target.checked)}
          />
          Delivery issues
        </label>

        {anyFilter && (
          <button
            type="button"
            className="btn btn-sm btn-icon"
            onClick={clearFilters}
            title="Clear all filters"
            aria-label="Clear all filters"
          >
            <Icon name="clearFilter" />
          </button>
        )}

        {/* Exports the filters above, not the page below: the file is what
            the person is looking at, however many pages it runs to.

            A select rather than two buttons: choosing a format is one
            decision, and two buttons put it to the operator twice. It
            returns to 'Export' afterwards so it reads as an action. */}
        <div className="toolbar-end">
          <select
            aria-label="Export the filtered orders"
            className="export-select"
            value=""
            disabled={Boolean(exporting)}
            onChange={(e) => {
              const format = e.target.value
              if (format) runExport(format)
            }}
          >
            <option value="">{exporting ? 'Preparing…' : 'Export'}</option>
            <option value="csv">CSV</option>
            <option value="pdf">PDF</option>
          </select>
        </div>
      </Toolbar>

      <Alert tone="danger" message={error} onClose={() => setError('')} />

      {isLoading && <Loading />}
      {isError && <ErrorNote message="Could not load orders" onRetry={refetch} />}

      {data && !data.items.length && (
        reviewing ? (
          <Empty
            icon="✅"
            title="Nothing to review"
            hint="Every order so far was understood confidently."
          />
        ) : (
          <Empty
            title={
              filters.issue
                ? 'No open delivery issues'
                : filters.later
                ? 'No orders are waiting for delivery from before today'
                : 'No orders match these filters'
            }
          />
        )
      )}

      {data && data.items.length > 0 && (
        <>
          <table className="table">
            <thead>
              <tr>
                {COLUMNS.map((column) => {
                  const on = activeSort.replace(/^-/, '') === column.key
                  const descending = activeSort.startsWith('-')
                  return (
                    <th
                      key={column.key}
                      className={column.right ? 'right' : undefined}
                      aria-sort={on ? (descending ? 'descending' : 'ascending') : 'none'}
                    >
                      <button
                        type="button"
                        className={`sort-header${on ? ' active' : ''}`}
                        onClick={() => sortBy(column)}
                      >
                        {column.label}
                        <SortIcon state={on ? (descending ? 'desc' : 'asc') : 'none'} />
                      </button>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {data.items.map((order) => (
                <tr key={order.id}>
                  <td>
                    {/* The order number alone. The quoted fragments that used
                        to hang under it were what matching failed to place —
                        out of context they said nothing a reader could act on
                        from a list, and they pushed every other row apart. */}
                    <Link to={`/orders/${order.id}`}>{order.order_number}</Link>
                  </td>
                  <td>
                    {order.customer_name}
                    {(order.customer_city || order.customer_phone) && (
                      <div className="cell-sub">
                        {[order.customer_city, order.customer_phone]
                          .filter(Boolean)
                          .join(' | ')}
                      </div>
                    )}
                  </td>
                  <td>
                    <SourceBadge source={order.source} />
                  </td>
                  <td>
                    <StatusBadge status={order.status} />
                    {order.delivery_issue_open && (
                      <div className="cell-sub">
                        <span className="tag-issue">Items not delivered</span>
                      </div>
                    )}
                  </td>
                  <td className="right">{money(order.grand_total)}</td>
                  <td className="muted">{dateTime(order.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <Pagination
            page={data.page}
            pageSize={data.page_size}
            total={data.total}
            onChange={setPage}
          />
        </>
      )}
    </div>
  )
}
